import Image from '@tiptap/extension-image'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatImageAlt, parseImageAlt } from '@caliu/utils'
import { ImageDialog } from '../components/editor/ImageDialog'
import {
  prepareImageForInsertion,
  uploadImageAndReplace,
} from '../lib/uploadImage'
import {
  getLocalAttachment,
  subscribeLocalAttachment,
} from '../lib/localAttachmentCache'
import { Button } from '../components/ui/Button'
import { ToolbarPill } from '../components/ui/ToolbarPill'
import { Icon } from "@/components/ui/Icon";

function ImageNodeView({ node, updateAttributes, deleteNode, editor, getPos }: NodeViewProps) {
  const { t } = useTranslation()
  const src = node.attrs.src as string
  const alt = node.attrs.alt as string | undefined
  const width = node.attrs.width as number | null
  const placeholder = node.attrs.placeholder as string | null

  // Images inside a table cell render as a "regular editor with limited
  // functions": capped to the column width and a max height so a tall image
  // can't balloon the row, and with the resize handle disabled (width is
  // column-bound). Detected by walking the node's ancestors.
  const inCell = ((): boolean => {
    try {
      const pos = getPos?.()
      if (pos == null) return false
      const $pos = editor.state.doc.resolve(pos)
      for (let d = $pos.depth; d > 0; d--) {
        const name = $pos.node(d).type.name
        if (name === 'tableCell' || name === 'tableHeader') return true
      }
    } catch {
      // getPos can throw if the node was just removed; treat as not-in-cell.
    }
    return false
  })()
  const CELL_IMAGE_MAX_HEIGHT = 200

  const [hovered, setHovered] = useState(false)
  const [resizeHovered, setResizeHovered] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Local-display cache: if this URL's bytes have been primed before the
  // upload lands, show the blob URL instead of hitting the network.
  const [displaySrc, setDisplaySrc] = useState<string>(() =>
    getLocalAttachment(src) ?? src,
  )

  useEffect(() => {
    setDisplaySrc(getLocalAttachment(src) ?? src)
    const unsubscribe = subscribeLocalAttachment((url) => {
      if (url === src) setDisplaySrc(getLocalAttachment(src) ?? src)
    })
    return unsubscribe
  }, [src])

  const containerRef = useRef<HTMLDivElement>(null)

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const container = containerRef.current
    if (!container) return

    const startX = e.clientX
    const startWidth = container.getBoundingClientRect().width
    const parentWidth = (container.parentElement?.getBoundingClientRect().width ?? startWidth)

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX
      const newPct = Math.round(Math.min(100, Math.max(20, ((startWidth + dx) / parentWidth) * 100)))
      updateAttributes({ width: newPct })
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  return (
    // Vertical spacing lives in editor.css (a top-level image: 20 above, 32
    // below). The inline-block sits on the line's top, not its baseline, so
    // no descender gap adds to it.
    <NodeViewWrapper as="div" style={{ display: 'block' }}>
      <div
        ref={containerRef}
        style={{ position: 'relative', display: 'inline-block', verticalAlign: 'top', width: inCell ? 'auto' : width ? `${width}%` : '100%', maxWidth: '100%', maxHeight: inCell ? CELL_IMAGE_MAX_HEIGHT : undefined, overflow: 'hidden', borderRadius: '10px' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setResizeHovered(false) }}
      >
        {/* LQIP blur-up placeholder */}
        {placeholder && !loaded && (
          <img
            src={placeholder}
            alt=""
            aria-hidden
            style={{
              width: '100%',
              display: 'block',
              borderRadius: '10px',
              filter: 'blur(20px)',
              transform: 'scale(1.05)',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          />
        )}
        <img
          src={displaySrc}
          alt={alt ?? ''}
          data-drag-handle=""
          onLoad={() => setLoaded(true)}
          style={{
            width: inCell ? 'auto' : '100%',
            maxWidth: '100%',
            maxHeight: inCell ? CELL_IMAGE_MAX_HEIGHT : undefined,
            objectFit: inCell ? 'contain' : undefined,
            display: 'block',
            borderRadius: '10px',
            opacity: placeholder && !loaded ? 0 : 1,
            transition: 'opacity 0.3s ease',
          }}
        />

        {/* Hover action bar */}
        <ToolbarPill
          className={`transition-opacity duration-150 ${hovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          style={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Button
            variant="ghost"
            size="icon"
            title={t('image.download')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const link = document.createElement('a')
              link.href = src
              link.download = alt || 'image'
              link.click()
            }}
            className="text-muted"
          >
            <Icon name="download" size={14} />
          </Button>
          {editor.isEditable && (
            <Button
              variant="ghost"
              size="icon"
              title={t('image.replace')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setDialogOpen(true)}
              className="text-muted"
            >
              <Icon name="sync" size={14} />
            </Button>
          )}
          {editor.isEditable && (
            <Button
              variant="ghost"
              size="icon"
              title={t('image.remove')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => deleteNode()}
              className="text-muted hover:bg-danger-bg hover:text-danger"
            >
              <Icon name="trash" size={14} />
            </Button>
          )}
        </ToolbarPill>

        {/* Resize handle — hidden in cells (width is column-bound) and in read-only notes (resize is a write action) */}
        {editor.isEditable && !inCell && (
          <div
            style={{
              position: 'absolute',
              right: -4,
              top: 0,
              bottom: 0,
              width: 8,
              cursor: 'ew-resize',
              display: 'flex',
              alignItems: 'stretch',
            }}
            onMouseEnter={() => setResizeHovered(true)}
            onMouseLeave={() => setResizeHovered(false)}
            onMouseDown={handleResizeMouseDown}
          >
            <div
              style={{
                width: 4,
                margin: 'auto 0',
                height: resizeHovered ? '40%' : 0,
                borderRadius: 2,
                transition: 'height 0.15s',
              }}
              className="bg-accent"
            />
          </div>
        )}
      </div>

      <ImageDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onInsert={(newSrc, newAlt, file) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const noteId = ((editor.storage as any).imageUpload as { noteId?: string } | undefined)?.noteId
          if (file && noteId) {
            const { attachmentId, finalURL } = prepareImageForInsertion(file)
            updateAttributes({ src: finalURL, alt: newAlt ?? '' })
            setDialogOpen(false)
            void uploadImageAndReplace(editor, file, noteId, finalURL, attachmentId)
          } else {
            updateAttributes({ src: newSrc, alt: newAlt ?? '' })
            setDialogOpen(false)
          }
        }}
      />
    </NodeViewWrapper>
  )
}

export const ImageExtension = Image.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(
          state: {
            esc: (text: string, startOfLine?: boolean) => string
            write: (text: string) => void
          },
          node: {
            attrs: { alt?: unknown; src?: unknown; title?: unknown; width?: unknown }
          },
        ) {
          // Every code path we control writes string attrs, but production
          // crashes ("e.replace is not a function") prove non-strings reach
          // this hook — the DOM ProseMirror parses can carry attrs we never
          // wrote (pasted content, browser extensions mutating the editor).
          // A throw here aborts getMarkdown() for the whole note and kills
          // autosave with it, so coerce instead of trusting the types.
          const text = (value: unknown): string =>
            typeof value === 'string' ? value : value == null ? '' : String(value)
          const width = typeof node.attrs.width === 'number' ? node.attrs.width : null
          const alt = formatImageAlt(text(node.attrs.alt), width)
          const title = text(node.attrs.title)
          state.write(
            `![${state.esc(alt)}](${text(node.attrs.src).replace(/[\(\)]/g, '\\$&')}${
              title ? ` "${title.replace(/"/g, '\\"')}"` : ''
            })`,
          )
        },
        parse: {
          updateDOM(element: HTMLElement) {
            element.querySelectorAll('img[src]').forEach((image) => {
              const { alt, width } = parseImageAlt(image.getAttribute('alt'))
              image.setAttribute('alt', alt)
              if (width != null) {
                image.setAttribute('data-width', String(width))
              } else {
                image.removeAttribute('data-width')
              }
            })
          },
        },
      },
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute('data-width')
          if (v) return parseInt(v, 10)
          return parseImageAlt(el.getAttribute('alt')).width ?? null
        },
        renderHTML: () => ({}),
      },
      placeholder: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-placeholder'),
        renderHTML: (attrs) =>
          attrs.placeholder ? { 'data-placeholder': attrs.placeholder } : {},
      },
    }
  },

  draggable: true,

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView)
  },
}).configure({ inline: false })
